import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { localeTag } from '@/lib/localeTag';
import { isolate } from '../format';
import type { LoyaltyHistoryEntry } from '../loyaltyApi';

interface Props {
  entries: LoyaltyHistoryEntry[];
  /** Restaurant timezone (IANA). */
  tz: string;
}

function labelOf(t: TFunction, entry: LoyaltyHistoryEntry, lng: string): string {
  if (entry.kind === 'earn') return t('loyalty.history.earn');
  if (entry.kind === 'redeem') {
    return entry.reward_name
      ? t('loyalty.history.redeem', { name: isolate(entry.reward_name, lng) })
      : t('loyalty.history.redeemNoName');
  }
  if (entry.note === 'expired_refund') return t('loyalty.history.expiredRefund');
  if (entry.note === 'counter_cancel') return t('loyalty.history.counterCancel');
  // SC-1622 — the order was cancelled, abandoned or refunded: its points went back out.
  if (entry.note === 'order_voided') return t('loyalty.history.orderVoided');
  return t('loyalty.history.adjust');
}

/** The sign the customer reads: a spend is « − », an earn « + », an adjustment as stored. */
function signedPoints(entry: LoyaltyHistoryEntry): number {
  if (entry.kind === 'earn') return Math.abs(entry.points);
  if (entry.kind === 'redeem') return -Math.abs(entry.points);
  return entry.points;
}

/** « Historique des points » — the last movements, newest first (SC-1091, M11). */
export default function LoyaltyHistory({ entries, tz }: Props) {
  const { t, i18n } = useTranslation();
  const fmt = new Intl.DateTimeFormat(localeTag(i18n.language), {
    day: '2-digit',
    month: '2-digit',
    timeZone: tz,
  });

  return (
    <section data-testid="loyalty-history" aria-labelledby="loyalty-history-title">
      <h3 id="loyalty-history-title" className="m-0 mb-3 font-display text-lg text-ink">
        {t('loyalty.history.title')}
      </h3>
      {entries.length === 0 ? (
        <p className="m-0 rounded-btn border-2 border-dashed border-[var(--ink-faint)] p-5 text-center text-sm text-ink-mute">
          {t('loyalty.history.empty')}
        </p>
      ) : (
        <ul className="m-0 list-none rounded-btn border-2 border-solid border-ink bg-paper p-0">
          {entries.map((entry, i) => {
            const points = signedPoints(entry);
            return (
              <li
                key={`${entry.created_at}-${i}`}
                className={`flex items-center gap-3 px-3 py-3 ${i > 0 ? 'border-0 border-t border-solid border-hairline' : ''}`}
              >
                <span className="w-12 flex-none font-mono text-xs text-ink-mute">
                  {fmt.format(new Date(entry.created_at))}
                </span>
                <span className="min-w-0 flex-1">
                  <span dir="auto" className="block break-words text-sm text-ink">
                    {labelOf(t, entry, i18n.language)}
                  </span>
                  {entry.channel !== 'online' && (
                    <span className="block font-mono text-[11px] text-ink-mute">
                      {t(`loyalty.history.channel.${entry.channel}`)}
                    </span>
                  )}
                </span>
                <span
                  dir="ltr"
                  className={`flex-none font-mono text-sm font-bold text-ink`}
                >
                  {points > 0 ? '+' : points < 0 ? '−' : ''}
                  {Math.abs(points)}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
}
